import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  PageTabs,
  saveErrorReason,
  useBatchSelection,
  withErrorReason,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import useTheme from "@common/hooks/useTheme";
import { useServiceConditionsList, useServiceConditionMutations } from "../api";
import {
  buildServiceConditionBatchActions,
  type ServiceConditionTab,
} from "./batchActions";
import type { ServiceCondition } from "../types";

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [tab, setTab] = useState<ServiceConditionTab>("active");

  // Required = standard green (teal), Optional = amber; white text for legibility
  // (the default primary/secondary variants rendered dark text on dark fills).
  const statusPillStyle = (status: "required" | "optional") => ({
    backgroundColor:
      status === "required"
        ? theme.colors.teal["2"]
        : theme.colors.yellow.primary,
    color: theme.colors.white,
  });

  const sel = useBatchSelection([tab]);
  const { archive, restore, move } = useServiceConditionMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } =
    useServiceConditionsList({
      page: { size: 500 },
      filter: { active: tab === "active" },
    });

  const selected = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<ServiceCondition>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "status",
      header: "Required / Optional",
      cell: ({ row }) =>
        row.original.status === "required" ? (
          // Plain `.badge` span (no `bg-*` utility) so our inline background wins;
          // Bootstrap's `.bg-*` classes set background-color with !important.
          <span className="badge" style={statusPillStyle("required")}>
            Required
          </span>
        ) : (
          <span className="badge" style={statusPillStyle("optional")}>
            Optional
          </span>
        ),
    },
  ];

  const runOnSelected = async (
    fn: (s: ServiceCondition) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selected];
    const failures: BatchFailure[] = [];
    for (const s of targets) {
      try {
        await fn(s);
      } catch (err) {
        failures.push({ label: s.name, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} item(s).`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (s) => archive(s.id),
        (n) => `${n} item(s) archived.`
      );
    } else {
      await runOnSelected(
        (s) => restore(s.id, s.name),
        (n) => `${n} item(s) restored.`
      );
    }
  };

  const handleReorder = async (
    moves: Array<{ id: string | number; position: number }>
  ) => {
    try {
      for (const { id, position } of moves) {
        await move(String(id), position);
      }
      refetch();
    } catch (err) {
      setToast({
        variant: "danger",
        text: withErrorReason(
          "Could not reorder. The list has been refreshed.",
          err
        ),
      });
      refetch();
    }
  };

  const batchActions = buildServiceConditionBatchActions(
    tab,
    sel.selectedIds.length,
    {
      onDelete: () => setConfirmOpen(true),
      onRestore: () => setConfirmOpen(true),
    }
  );

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Terms and Conditions"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Term or Condition"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as ServiceConditionTab);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          draggable={tab === "active"}
          onReorder={tab === "active" ? handleReorder : undefined}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No terms or conditions found."
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={tab === "active" ? "Archive items?" : "Restore items?"}
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} item(s)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
